import React, { useEffect, useRef, useState } from 'react';
import ClearIcon from '../assets/icons/clear.svg';
import SearchIcon from '../assets/icons/search.svg';
import { FadeLoader } from 'react-spinners';
import { SEARCH_INPUT_DEBOUNCE_MS, useDebounce } from '../helper/customHooks';
import Pagination from "../ui_components/pagination";
import { adminLogin, getUsers } from "../api/users";
import { getCustomerPlanInfo } from "../api/stripe";
import { formatMDY } from "../utils/dateFormatter";
import { writeCachedPricingPermission } from '../helper/pricingPermission';
import { getAccount } from '../api/account';

const TEAM_SETTINGS_PLAN_ACCESS_CACHE_PREFIX = 'team-settings-plan-access-';

const toPositiveInteger = (value: any): number => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 0;
};

const resolveOwnerAccountId = (account: any): number => {
  const ownerAccountId = toPositiveInteger(account?.ownerAccountId);
  if (ownerAccountId) {
    return ownerAccountId;
  }

  const parentAccountId = toPositiveInteger(account?.parentAccountId);
  if (!account?.isOwner && parentAccountId) {
    return parentAccountId;
  }

  return 0;
};

const AllUsersPage = (props: any) => {
  // ####### REFS #######
  const searchInputRef = useRef<any>();
  const dotsMenuRef = useRef<any>();
  const dotsMenuRefContainer = useRef<any>();
  const submenuRef = useRef<any>();
  const confirmModalRef = useRef<any>();
  const dropdown_ref: any = {
    client: useRef<any>(),
    status: useRef<any>(),
    estimate: useRef<any>(),
    invoice: useRef<any>(),
  };
  // ####### END OF REFS #######
  // ####### STATES #######
  const [filter_dropdown, setFilterDropdown] = useState<any>('');
  const [isSubmenuOpened, setIsSubmenuOpened] = useState<boolean>(false);
  const [tableData, setTableData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isLoging, setIsLogin] = useState<boolean>(false);
  const [openDropdown, setOpenDropdown] = useState<any>({
    column: '',
    row_id: '',
  });
  const [searchTerm, setSearchTerm] = useState<any>('');
  const [searchSubmitTrigger, setSearchSubmitTrigger] = useState(0);
  const [pagination, setPagination] = useState<{ pageNumber: number, totalPages: number, dataLength: number, pageLimit: number }>({ pageNumber: 1, totalPages: 1, dataLength: 1, pageLimit: 10 });
  const debouncedSearch = useDebounce(searchTerm, SEARCH_INPUT_DEBOUNCE_MS, searchSubmitTrigger);

  // ####### END OF STATES #######
  // ####### FUNCTIONS #######
  const handleChangeValueSearchInput = (e: any) => {
    const { value } = e.target;
    setSearchTerm(value);
  };

  const handleSearchInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    setSearchSubmitTrigger(prev => prev + 1);
  };

  const handleClearSearchKeyword = () => {
    setSearchTerm('');
    setPagination(prev => ({ ...prev, pageNumber: 1 }));
  };

  const handleCloseFilterDropdown = () => {
    setFilterDropdown('');
  };

  const handleCloseDropdown = (event: any) => {
    let ref_name = '';
    switch (filter_dropdown) {
      case 'client_filter':
        ref_name = 'client';
        break;
      case 'status_filter':
        ref_name = 'status';
        break;
      case 'estimate_filter':
        ref_name = 'estimate';
        break;
      case 'invoice_filter':
        ref_name = 'invoice';
        break;
      default:
        break;
    }
    const ref = dropdown_ref[ref_name];
    if (ref && ref.current && !ref.current.contains(event.target)) {
      handleCloseFilterDropdown();
    }
  };

  const handleClickOutside = (event: MouseEvent): void => {
    if (submenuRef && submenuRef.current && !submenuRef.current.contains(event.target as Node)) {
      setIsSubmenuOpened(false);
    }
  };

  const handleClickOutsideDropdown = (event: MouseEvent): void => {
    if (
      dotsMenuRef &&
      dotsMenuRef.current &&
      !dotsMenuRef.current.contains(event.target as Node) &&
      dotsMenuRefContainer &&
      dotsMenuRefContainer.current &&
      !dotsMenuRefContainer.current.contains(event.target as Node) &&
      confirmModalRef &&
      confirmModalRef.current &&
      !confirmModalRef.current.contains(event.target as Node)
    ) {
      setOpenDropdown({
        column: '',
        row_id: '',
      });
    }
  };

  const handleLogInAsUser = async (userParam: any) => {
    setIsLogin(true);
    const accountId = Number(localStorage.getItem('accountId'));
    const res = await adminLogin({ adminAccountId: accountId, userId: userParam.id })

    const { account, user, token, refreshToken } = res;

    const baseUserObj = {
      email: user.email,
      token,
      refreshToken,
      item_order: '',
      type: account.type,
      isBeta: account.isBeta,
    };

    localStorage.setItem('quipa-store', JSON.stringify({ user: baseUserObj }));
    localStorage.setItem('accountId', account.id);
    localStorage.removeItem('ownerAccountId');
    localStorage.removeItem('subaccountCanSeePricing');

    let resolvedAccount = account;
    if (!resolveOwnerAccountId(account) || account.canSeePricing === undefined) {
      try {
        const accountRes = await getAccount(account.id);
        if (accountRes && !accountRes.error) {
          resolvedAccount = {
            ...account,
            ...accountRes,
          };
        }
      } catch (error) {
        console.error('Failed to resolve impersonated account scope:', error);
      }
    }

    const ownerAccountId = resolveOwnerAccountId(resolvedAccount);
    const canSeePricing = !!resolvedAccount.canSeePricing;
    writeCachedPricingPermission(canSeePricing);
    if (ownerAccountId) {
      localStorage.setItem('ownerAccountId', String(ownerAccountId));
      localStorage.setItem('subaccountCanSeePricing', canSeePricing ? '1' : '0');
      localStorage.setItem(`${TEAM_SETTINGS_PLAN_ACCESS_CACHE_PREFIX}${account.id}`, '1');
    } else {
      localStorage.removeItem('ownerAccountId');
      localStorage.removeItem('subaccountCanSeePricing');
    }

    let item_order = '';
    const planInfoRes = await getCustomerPlanInfo(ownerAccountId || account.id);
    let planInfo = planInfoRes['main']
    item_order =
      planInfo && planInfo.product_metadata && planInfo.product_metadata.item_order
        ? planInfo.product_metadata.item_order.toString()
        : '';

    const userObj = {
      ...baseUserObj,
      item_order,
    };

    localStorage.setItem('quipa-store', JSON.stringify({ user: userObj }));
    localStorage.setItem('firstName', user.firstName);
    localStorage.setItem('lastName', user.lastName);
    localStorage.setItem('role', user.role);
    localStorage.setItem('avatarId', account.avatarId);
    localStorage.setItem('status', account.type);
    localStorage.setItem('isBeta', account.isBeta.toString());
    localStorage.setItem('createdAt', account.createdAt);
    if (user.logoId) {
      localStorage.setItem('logoId', user.logoId);
    }
    if (account.customerId) {
      localStorage.setItem('customerId', account.customerId);
    }
    localStorage.removeItem('appLoaded');

    if (account.loginAllowed) {
      localStorage.setItem(`login-event-${account.id}`, 'login' + Math.random());
      window.location.replace('/projects');
    } else {
      window.location.replace('/projects');
    }

  };

  const highlightText = (text: any, searchTerm: any, delay?: number) => {
    if (!searchTerm) return text;
    if (!searchTerm.trim()) return text;

    const escapedSearchTerm = searchTerm.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

    const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part: any, index: any) =>
      regex.test(part) ? (
        <span key={index} className="searchTerm">
          {part}
        </span>
      ) : (
        part
      )
    );
  };
  // ####### END OF FUNCTIONS #######

  // ####### FETCH FUNCTIONS #######
  const fetchUsers = async () => {
    setLoading(true);
    const { pageNumber, pageLimit } = pagination;

    const res = await getUsers({
      page: pageNumber,
      limit: pageLimit,
      search: searchTerm
    });
    if (res && !res.error) {
      setTableData(res.items);

      const totalCount = res.totalCount || res.items.length;
      const totalPages = Math.ceil(totalCount / pageLimit);

      setPagination(prev => ({
        ...prev,
        dataLength: totalCount,
        totalPages: totalPages === 0 ? 1 : totalPages,
      }));
    } else {
      setTableData([]);
      setPagination(prev => ({ ...prev, dataLength: 0, totalPages: 1, pageNumber: 1 }));
    }
    setLoading(false);
    if (searchInputRef && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // ####### END OF FETCH FUNCTIONS #######
  // ####### USE EFFECT #######
  useEffect(() => {
    fetchUsers();
  }, [pagination.pageNumber, pagination.pageLimit, debouncedSearch]);

  useEffect(() => {
    document.addEventListener('mousedown', handleCloseDropdown);

    return () => {
      document.removeEventListener('mousedown', handleCloseDropdown);
    };
  }, [filter_dropdown]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isSubmenuOpened]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutsideDropdown);

    return () => {
      document.removeEventListener('click', handleClickOutsideDropdown);
    };
  }, [openDropdown.column, openDropdown.row_id]);

  // ####### END OF USE EFFECT #######
  return (
    <div className="all_users_page_container">
      <header className="projects_header">
        <div className="all_users_header">
          <div className="all_users_title">
            <span>Customer Management ({tableData.length})</span>
          </div>
          <div className="all_users_subtitle">
            <span>Select a customer to sign in as and view their account</span>
          </div>
        </div>

        <div className="filters_container">
          <div id="full-width-search" className="search_container">
            {searchTerm ? (
              <button type="button" onClick={() => handleClearSearchKeyword()}>
                <ClearIcon style={{ width: '16px', fill: '#3b87a0', cursor: 'pointer' }} />
              </button>
            ) : (
              <SearchIcon style={{ width: '16px', height: '25px', fill: '#3b87a0' }} />
            )}
            <input
              ref={searchInputRef}
              value={searchTerm || ''}
              onChange={(e) => handleChangeValueSearchInput(e)}
              onKeyDown={handleSearchInputKeyDown}
              className="search_input"
              placeholder="Search by company, name, or email..."
            />
          </div>
        </div>

        <div className="all_users_subtitle" style={{marginBottom:'15px'}}>
          <span>Showing {pagination.pageLimit} of {pagination.dataLength} customers</span>
        </div>
      </header>

      {loading ? (
        <FadeLoader
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(50%,-50%)',
            zIndex: 9999,
            display: !loading ? 'none' : 'block',
          }}
          color="#3B97B1"
          aria-label="Loading Spinner"
          data-testid="loader"
        />
      ) : (
        <div className="all_users_table">
          <div className="thead">
            <div className="cell"></div>

            <div className="text-left cell">
              <div
                className="inline-flex items-center cursor-pointer"
              >
                <span>Company Name</span>
              </div>
            </div>

            <div className="text-left cell">
              <div
                className="inline-flex items-center cursor-pointer"
              >
                <span>First name</span>
              </div>
            </div>

            <div className="text-left cell">
              <div
                className="inline-flex items-center cursor-pointer"
              >
                <span>Last name</span>
              </div>
            </div>


            <div className="text-left cell">
              <div
                className="inline-flex items-center cursor-pointer"
              >
                <span>Email</span>
              </div>
            </div>

            <div className="cell">
              <div
                className="inline-flex items-center cursor-pointer"
              >
                <span>Created Date</span>
              </div>
            </div>

            <div className="cell">
              <div
                className="inline-flex items-center cursor-pointer"
              >
                <span>Actions</span>
              </div>
            </div>
          </div>

          <div className="tbody">
            {tableData && tableData.length > 0 ? (
              tableData.map((item: any) => {
                const user = item;
                return (
                  <div className="tr" key={item.id}>
                    <div className="cell"></div>

                    <div className="text-left td">
                      <div>
                        <span className="user_field_all_users">
                          {highlightText((user && user.billingCompanyName) || '', debouncedSearch)}
                        </span>
                      </div>
                    </div>

                    <div className="text-left td">
                      <div>
                        <span className="user_field_all_users">
                          {highlightText((user && user.firstName) || '', debouncedSearch)}
                        </span>
                      </div>
                    </div>

                    <div className="text-left td">
                      <div>
                        <span className="user_field_all_users">
                          {highlightText((user && user.lastName) || '', debouncedSearch)}
                        </span>
                      </div>
                    </div>

                    <div className="text-left td">
                      <div>
                        <span className="user_field_all_users">
                          {highlightText((user && user.email) || '', debouncedSearch)}
                        </span>
                      </div>
                    </div>

                    <div className="text-left td">
                      <div>
                        <span className="user_field_all_users">
                          {highlightText((formatMDY(user && user.createdAt)) || '', debouncedSearch)}
                        </span>
                      </div>
                    </div>

                    <div className="td">
                      <div className="flex relative">
                        <button className='primary_btn' onClick={() => handleLogInAsUser(user)} >
                          Sign In As
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : null}
          </div>

          {isLoging && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FadeLoader
                color="#3B97B1"
                aria-label="Loading Spinner"
                data-testid="login-loader"
              />
            </div>
          )}

          <Pagination
            pagination={pagination}
            loading={loading}
            setPagination={setPagination}
          ></Pagination>
        </div>
      )
      }
    </div >
  );
};

export default AllUsersPage;
